import { useMutation } from '@tanstack/react-query';
import type { z } from 'zod';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';
import { generateAiDraft } from '#src/app/pages/startAPetitionV2/shared/api';
import { useImageJob } from '#src/app/pages/startAPetitionV2/shared/hooks';

import type { schema } from '../../schema';

type FormData = z.infer<typeof schema>;

export function useGenerateAiDraft() {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const { mutateAsync: createJobAsync } = useImageJob();

	const { mutate, error, ...mutationState } = useMutation({
		mutationFn: async (formData: FormData) => {
			// TODO: remove non-null assertion with schema validation _just incase_
			const input = { mainPrompt: data.draft?.input?.issuePrompt ?? '', personalStory: formData.personalStory };

			const [aiDraftResponse, imageResponse] = await Promise.allSettled([
				generateAiDraft(input, utilityContext, track),
				createJobAsync(input),
			]);

			if (aiDraftResponse.status === 'rejected') throw aiDraftResponse.reason;

			const image =
				imageResponse.status === 'fulfilled' ? { image: { responseId: imageResponse.value.jobId } } : undefined;

			return { ...aiDraftResponse.value, ...image };
		},
		onMutate: (formData) => {
			void track('sap_personal_story_submit', { personal_story: formData.personalStory });
		},
		onSuccess: (response) => {
			const recommendedDraft = { ask: response.ask, description: response.description, image: null };
			const metadata = { imageJobId: response.image?.responseId ?? null };
			actions.send({
				type: 'submit',
				draft: { recommendedDraft, petitionDraft: recommendedDraft, editingDraft: null, metadata },
			});
		},
	});

	return { mutate, error, mutationState };
}
